import React, { useEffect, useState } from "react";
import { Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { reApi, errMsg, fmtDateTime } from "@/lib/residentApi";
import { inputCls, labelCls } from "./ui";

let reasonsP = null;
const getReasons = () => (reasonsP ||= reApi.get("/residents/archive/reasons").catch((e) => { reasonsP = null; throw e; }));

/** Archive (required reason + notes) / Unarchive for one resident; hidden without the archive_records permission. */
export function ArchivePanel({ resident, onChanged }) {
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ reason: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { getReasons().then(setMeta).catch(() => {}); }, []);
  const label = (k) => meta?.reasons.find((x) => x.key === k)?.label || k || "No reason recorded";
  const archived = resident.status === "archived";

  const run = async (path, body, msg) => {
    setBusy(true);
    try { const out = await reApi.post(`/residents/${resident.id}/${path}`, body); toast.success(msg(out)); setOpen(false); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const archive = () => run("archive", f, (o) => {
    const c = o.closed || {};
    return `Archived · ${c.appointments_cancelled || 0} appointment(s) cancelled, ${c.group_sessions_left || 0} group session(s) left, ${c.homework_closed || 0} homework closed`;
  });

  if (archived) {
    return (
      <div data-testid="resident-archived-banner" className="mt-3 p-3 border border-amber-400/40 bg-amber-500/10 rounded-sm flex flex-wrap items-center gap-3 text-sm text-amber-100">
        <Archive className="h-4 w-4 text-amber-300" />
        <span className="flex-1 min-w-[200px]" data-testid="resident-archived-info">
          Archived · {label(resident.archive_reason)}{resident.archived_at ? ` · ${fmtDateTime(resident.archived_at)}` : ""}
          {resident.archived_by?.name || resident.archived_by?.email ? ` · by ${resident.archived_by.name || resident.archived_by.email}` : ""}
          {resident.archive_notes ? <span className="block text-amber-100/70 text-xs mt-0.5">{resident.archive_notes}</span> : null}
        </span>
        {meta?.can_archive && (
          <button type="button" disabled={busy} onClick={() => run("unarchive", undefined, () => "Unarchived · resident is active again")} data-testid="resident-unarchive-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-300/60 text-amber-200 font-mono text-[10px] tracking-[0.18em] uppercase rounded-sm hover:bg-amber-500/10 disabled:opacity-40">
            <ArchiveRestore className="h-3 w-3" /> Unarchive · return resident
          </button>
        )}
      </div>
    );
  }
  if (!meta?.can_archive) return null;
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="resident-archive-btn"
        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-nex-muted font-mono text-[10px] tracking-[0.18em] uppercase rounded-sm hover:text-amber-200 hover:border-amber-300/50">
        <Archive className="h-3 w-3" /> Archive resident
      </button>
    );
  }
  return (
    <div data-testid="resident-archive-form" className="mt-3 p-4 border border-amber-400/40 bg-amber-500/[0.06] rounded-sm space-y-3">
      <p className="text-xs text-nex-silver/80">Their bed is freed, upcoming appointments are cancelled, open homework is closed and portal sign-in turns off. Money, notes, documents and history stay exactly as they are.</p>
      <label className="block"><span className={labelCls}>Reason (required)</span>
        <select value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="resident-archive-reason" className={inputCls}>
          <option value="">Choose a reason…</option>
          {meta.reasons.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select></label>
      <label className="block"><span className={labelCls}>Notes (optional)</span>
        <textarea rows={2} maxLength={4000} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="resident-archive-notes" className={inputCls} /></label>
      <div className="flex items-center gap-2">
        <button type="button" disabled={busy || !f.reason} onClick={archive} data-testid="resident-archive-confirm"
          className="px-3 py-1.5 border border-amber-300/60 text-amber-200 font-mono text-[10px] tracking-[0.18em] uppercase rounded-sm hover:bg-amber-500/10 disabled:opacity-40">Archive resident</button>
        <button type="button" onClick={() => setOpen(false)} data-testid="resident-archive-cancel" className="font-mono text-[10px] tracking-[0.18em] uppercase text-nex-muted hover:text-nex-silver">Cancel</button>
      </div>
    </div>
  );
}
